import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Building2, Upload, Save, Mail, Eye, EyeOff, Send, Plus, X, Check, Pencil } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import RIBManagement from '@/components/RIBManagement';

const CompanySettings = () => {
  const [settings, setSettings] = useState({
    company_name: '',
    address: '',
    postal_code: '',
    city: '',
    phone: '',
    email: '',
    siret: '',
    tva_note: '',
    logo_white_base64: null,
    logo_black_base64: null,
    show_prices_in_questionnaire: true,
    questionnaire_email: '',
    auto_send_devis: true,
    auto_send_receipts: true,
    sender_emails: ['kolnoa.clients@gmail.com', 'kolnoaprod@gmail.com', 'jeremy.ferrar@gmail.com'],
    default_sender_email: 'kolnoaprod@gmail.com'
  });
  const [newEmailInput, setNewEmailInput] = useState('');
  const [editingEmail, setEditingEmail] = useState(null);
  const [editEmailInput, setEditEmailInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/company-settings`, { withCredentials: true });
      setSettings(res.data);
    } catch (error) {
      console.error('Error loading company settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (field, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('L\'image doit faire moins de 2 Mo');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1]; // Remove data:image/png;base64, prefix
      handleInputChange(field, base64);
      toast.success('Logo chargé avec succès');
    };
    reader.onerror = () => {
      toast.error('Erreur lors du chargement du logo');
    };
    reader.readAsDataURL(file);
  };

  const handleAddEmail = () => {
    if (!newEmailInput) return;
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmailInput)) {
      toast.error('Format d\'email invalide');
      return;
    }
    
    // Check if email already exists
    const currentEmails = settings.sender_emails || [];
    if (currentEmails.includes(newEmailInput)) {
      toast.error('Cet email existe déjà');
      return;
    }
    
    setSettings(prev => ({
      ...prev,
      sender_emails: [...(prev.sender_emails || []), newEmailInput]
    }));
    setNewEmailInput('');
    toast.success('Email ajouté');
  };

  const handleRemoveEmail = (emailToRemove) => {
    // Don't allow removing if it's the default email
    if (emailToRemove === settings.default_sender_email) {
      toast.error('Impossible de supprimer l\'email par défaut. Choisissez d\'abord un autre email par défaut.');
      return;
    }
    
    setSettings(prev => ({
      ...prev,
      sender_emails: (prev.sender_emails || []).filter(email => email !== emailToRemove)
    }));
    toast.success('Email supprimé');
  };

  const handleStartEdit = (email) => {
    setEditingEmail(email);
    setEditEmailInput(email);
  };

  const handleCancelEdit = () => {
    setEditingEmail(null);
    setEditEmailInput('');
  };

  const handleSaveEdit = () => {
    if (!editEmailInput) return;
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editEmailInput)) {
      toast.error('Format d\'email invalide');
      return;
    }
    
    // Check if email already exists (except the one being edited)
    const currentEmails = settings.sender_emails || [];
    if (currentEmails.includes(editEmailInput) && editEmailInput !== editingEmail) {
      toast.error('Cet email existe déjà');
      return;
    }
    
    // Update the email
    setSettings(prev => ({
      ...prev,
      sender_emails: (prev.sender_emails || []).map(email => 
        email === editingEmail ? editEmailInput : email
      ),
      // Update default email if it was the one being edited
      default_sender_email: prev.default_sender_email === editingEmail ? editEmailInput : prev.default_sender_email
    }));
    
    setEditingEmail(null);
    setEditEmailInput('');
    toast.success('Email modifié');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/company-settings`, settings, { withCredentials: true });
      toast.success('Paramètres enregistrés avec succès');
      loadSettings(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving company settings:', error);
      toast.error('Erreur lors de l\'enregistrement des paramètres');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-stone-800 flex items-center gap-2">
          <Building2 className="h-7 w-7 text-amber-600" />
          Paramètres de l'entreprise
        </h1>
        <p className="text-stone-600 mt-2">
          Gérez les informations de votre entreprise affichées sur les documents (devis, factures, contrats, etc.)
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Informations générales</CardTitle>
          <CardDescription>
            Ces informations apparaîtront sur tous vos documents officiels
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="company_name">Nom de l'entreprise</Label>
            <Input
              id="company_name"
              value={settings.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              placeholder="KOLNOA PRODUCTION"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="address">Adresse</Label>
              <Input
                id="address"
                value={settings.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="99, Rue de Crimée"
              />
            </div>
            <div>
              <Label htmlFor="postal_code">Code postal</Label>
              <Input
                id="postal_code"
                value={settings.postal_code}
                onChange={(e) => handleInputChange('postal_code', e.target.value)}
                placeholder="75019"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="city">Ville</Label>
            <Input
              id="city"
              value={settings.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="Paris"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                value={settings.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="06.68.11.93.43"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={settings.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="kolnoaprod@gmail.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="siret">SIRET</Label>
              <Input
                id="siret"
                value={settings.siret}
                onChange={(e) => handleInputChange('siret', e.target.value)}
                placeholder="51171274700019"
              />
            </div>
            <div>
              <Label htmlFor="tva_note">Note TVA</Label>
              <Input
                id="tva_note"
                value={settings.tva_note}
                onChange={(e) => handleInputChange('tva_note', e.target.value)}
                placeholder="TVA non applicable, art. 293 B du CGI"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Logos de l'entreprise</CardTitle>
          <CardDescription>
            Téléchargez vos logos (PNG recommandé, max 2 Mo). Le logo blanc est utilisé sur les fonds sombres, le logo noir sur les fonds clairs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="logo_white">Logo blanc (fonds sombres)</Label>
              <div className="mt-2">
                {settings.logo_white_base64 && (
                  <div className="mb-4 p-4 bg-stone-800 rounded-lg flex items-center justify-center h-32">
                    <img 
                      src={`data:image/png;base64,${settings.logo_white_base64}`} 
                      alt="Logo blanc" 
                      className="max-h-24 max-w-full object-contain"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    id="logo_white"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleLogoUpload('logo_white_base64', e)}
                    className="flex-1"
                  />
                  <Button variant="outline" size="sm" asChild>
                    <label htmlFor="logo_white" className="cursor-pointer">
                      <Upload className="h-4 w-4" />
                    </label>
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="logo_black">Logo noir (fonds clairs)</Label>
              <div className="mt-2">
                {settings.logo_black_base64 && (
                  <div className="mb-4 p-4 bg-stone-100 rounded-lg flex items-center justify-center h-32 border border-stone-300">
                    <img 
                      src={`data:image/png;base64,${settings.logo_black_base64}`} 
                      alt="Logo noir" 
                      className="max-h-24 max-w-full object-contain"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    id="logo_black"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleLogoUpload('logo_black_base64', e)}
                    className="flex-1"
                  />
                  <Button variant="outline" size="sm" asChild>
                    <label htmlFor="logo_black" className="cursor-pointer">
                      <Upload className="h-4 w-4" />
                    </label>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Paramètres Questionnaire Public */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-amber-600" />
            Questionnaire Public (Landing Page)
          </CardTitle>
          <CardDescription>
            Configurez l'affichage et la réception des demandes de devis depuis le formulaire public
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <div className="flex items-center gap-2 font-medium text-stone-800">
                {settings.show_prices_in_questionnaire ? (
                  <Eye className="h-4 w-4 text-green-600" />
                ) : (
                  <EyeOff className="h-4 w-4 text-stone-400" />
                )}
                Afficher les prix dans le récapitulatif
              </div>
              <p className="text-sm text-stone-600 mt-1">
                {settings.show_prices_in_questionnaire 
                  ? "Les clients verront les montants des prestations sélectionnées"
                  : "Les prix seront masqués, seule la liste des prestations sera visible"}
              </p>
            </div>
            <Switch
              checked={settings.show_prices_in_questionnaire}
              onCheckedChange={(checked) => handleInputChange('show_prices_in_questionnaire', checked)}
            />
          </div>

          <div>
            <Label htmlFor="questionnaire_email">Email de réception des demandes</Label>
            <Input
              id="questionnaire_email"
              type="email"
              value={settings.questionnaire_email}
              onChange={(e) => handleInputChange('questionnaire_email', e.target.value)}
              placeholder="kolnoa.clients@gmail.com"
            />
            <p className="text-xs text-stone-500 mt-1">
              Les demandes de devis depuis le formulaire public seront envoyées à cette adresse
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Paramètres Envois Automatiques */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-amber-600" />
            Envois Automatiques par Email
          </CardTitle>
          <CardDescription>
            Choisissez si les documents sont envoyés automatiquement au client ou manuellement
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <div className="font-medium text-stone-800">Envoi automatique des devis</div>
              <p className="text-sm text-stone-600 mt-1">
                {settings.auto_send_devis
                  ? "Le devis PDF est envoyé automatiquement au client lors de sa création"
                  : "Vous devrez envoyer manuellement le devis via le bouton 'Envoyer par email'"}
              </p>
            </div>
            <Switch
              checked={settings.auto_send_devis}
              onCheckedChange={(checked) => handleInputChange('auto_send_devis', checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex-1">
              <div className="font-medium text-stone-800">Envoi automatique des récépissés</div>
              <p className="text-sm text-stone-600 mt-1">
                {settings.auto_send_receipts
                  ? "Le récépissé PDF est envoyé automatiquement au client lors de l'enregistrement d'un paiement"
                  : "Vous devrez envoyer manuellement le récépissé via le bouton 'Envoyer par email'"}
              </p>
            </div>
            <Switch
              checked={settings.auto_send_receipts}
              onCheckedChange={(checked) => handleInputChange('auto_send_receipts', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Gestion des adresses email d'envoi */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-amber-600" />
            Adresses Email d'Envoi
          </CardTitle>
          <CardDescription>
            Gérez les adresses email disponibles pour l'envoi de documents (devis, contrats, notes d'auteur, etc.)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Liste des emails avec sélection par défaut */}
          <div>
            <Label className="mb-3 block">Emails disponibles (sélectionnez l'email par défaut)</Label>
            <RadioGroup
              value={settings.default_sender_email}
              onValueChange={(value) => handleInputChange('default_sender_email', value)}
              className="space-y-2"
            >
              {(settings.sender_emails || []).map((email) => (
                <div key={email} className="flex items-center justify-between p-3 border rounded-lg hover:bg-stone-50">
                  {editingEmail === email ? (
                    // Mode édition
                    <div className="flex items-center gap-2 flex-1">
                      <Mail className="h-4 w-4 text-stone-400" />
                      <Input
                        value={editEmailInput}
                        onChange={(e) => setEditEmailInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                        className="flex-1 font-mono text-sm"
                        autoFocus
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSaveEdit}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelEdit}
                        className="text-stone-600 hover:text-stone-700 hover:bg-stone-100"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    // Mode affichage normal
                    <>
                      <div className="flex items-center gap-3 flex-1">
                        <RadioGroupItem value={email} id={`email-${email}`} />
                        <label htmlFor={`email-${email}`} className="flex items-center gap-2 cursor-pointer flex-1">
                          <Mail className="h-4 w-4 text-stone-400" />
                          <span className="font-mono text-sm">{email}</span>
                          {email === settings.default_sender_email && (
                            <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full">
                              Par défaut
                            </span>
                          )}
                        </label>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStartEdit(email)}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          title="Modifier"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {(settings.sender_emails || []).length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveEmail(email)}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            title="Supprimer"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Ajouter un nouvel email */}
          <div className="pt-4 border-t">
            <Label htmlFor="new_email">Ajouter une adresse email</Label>
            <div className="flex gap-2 mt-2">
              <Input
                id="new_email"
                type="email"
                value={newEmailInput}
                onChange={(e) => setNewEmailInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                placeholder="nouvelle.adresse@example.com"
                className="flex-1"
              />
              <Button
                onClick={handleAddEmail}
                variant="outline"
                className="border-amber-600 text-amber-600 hover:bg-amber-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter
              </Button>
            </div>
            <p className="text-xs text-stone-500 mt-2">
              Ces adresses seront disponibles lors de l'envoi de documents. L'email par défaut sera présélectionné.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* RIB Management */}
      <RIBManagement />

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
        </Button>
      </div>
    </div>
  );
};

export default CompanySettings;
